import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSplitPackage } from '../../hooks/useSplitPackage';
import { Package, Reception } from '../../models/Reception';

interface SplitPackageModalProps {
  reception: Reception;
  pkg: Package;
}

const SplitPackageModal = forwardRef<ModalCenterRef, SplitPackageModalProps>(({ reception, pkg }, ref) => {
  const { t } = useTranslation('receptionsModule');
  const { toast } = useToast();
  const splitMutation = useSplitPackage();

  const [splitQty, setSplitQty] = useState<number>(1);

  const handleCancel = () => {
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
    setSplitQty(1);
  };

  const handleSplit = async () => {
    if (!isValid) return;

    try {
      await splitMutation.mutateAsync({
        receptionId: reception.id,
        pkg,
        receptionStatus: reception.status,
        data: { split_qty: splitQty },
      });

      toast({
        title: t('modal.split.success_title'),
        description: t('modal.split.success_description'),
      });

      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.split.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = splitMutation.isPending;
  const remaining = pkg.quantity - splitQty;
  const isValid = splitQty > 0 && remaining > 0;
  const maxSplit = pkg.quantity - 1;

  const getPackageTypeLabel = () => {
    if (pkg.package_type === 'OTHER' && pkg.package_type_other) {
      return pkg.package_type_other;
    }
    return t(`package_types.${pkg.package_type.toLowerCase()}`);
  };

  return (
    <ModalCenter ref={ref} title="Dividir paquete" type="default" width="default">
      <div className="space-y-5">
        {/* Paquete actual */}
        <div className="p-4 bg-muted/30 rounded-lg">
          <p className="text-xs text-complementary-text mb-1">Paquete actual</p>
          <p className="text-lg font-medium">
            {getPackageTypeLabel()} · <span className="text-primary">{pkg.quantity} unidades</span>
          </p>
        </div>

        {/* Selector */}
        <div>
          <p className="text-sm mb-3">¿Cuántas unidades quieres separar en un nuevo paquete?</p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setSplitQty(Math.max(1, splitQty - 1))}
              disabled={splitQty <= 1 || isLoading}
              className="w-10 h-10 p-0 text-lg"
            >
              −
            </Button>
            <Input
              type="number"
              min={1}
              max={maxSplit}
              value={splitQty}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                setSplitQty(Math.min(Math.max(1, val), maxSplit));
              }}
              disabled={isLoading}
              className="text-center text-xl font-bold w-24 h-10"
            />
            <Button
              variant="outline"
              onClick={() => setSplitQty(Math.min(maxSplit, splitQty + 1))}
              disabled={splitQty >= maxSplit || isLoading}
              className="w-10 h-10 p-0 text-lg"
            >
              +
            </Button>
          </div>
        </div>

        {/* Resultado */}
        <div className="border rounded-lg overflow-hidden">
          <div className="text-xs text-complementary-text px-4 py-2 bg-muted/30">Después de dividir tendrás:</div>
          <div className="grid grid-cols-2 divide-x divide-border">
            <div className="p-4 text-center">
              <p className="text-xs text-complementary-text mb-1">Paquete original</p>
              <p className={`text-2xl font-bold ${remaining > 0 ? '' : 'text-destructive'}`}>{remaining}</p>
              <p className="text-xs text-complementary-text">unidades</p>
            </div>
            <div className="p-4 text-center bg-success/5">
              <p className="text-xs text-complementary-text mb-1">Nuevo paquete</p>
              <p className="text-2xl font-bold text-success">{splitQty}</p>
              <p className="text-xs text-complementary-text">unidades</p>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSplit} disabled={isLoading || !isValid}>
            {isLoading ? 'Dividiendo...' : 'Dividir'}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

SplitPackageModal.displayName = 'SplitPackageModal';

export default SplitPackageModal;
